/**
 * ClientStep - Step 1 of the Add Plane wizard
 *
 * Allows users to:
 * - Search and select an existing client
 * - Optionally select a contact for the selected client
 * - OR create a new client with mandatory contact
 */

import { useState, useMemo, useEffect, useRef } from 'react'
import { Check, ChevronsUpDown, Loader2, Plus, Search, User, Building2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

import { useClients } from '@/hooks/useClients'
import { useContacts } from '@/hooks/useContacts'
import {
  useAddPlaneWizardStore,
  type NewClientData,
  type NewContactData,
} from '@/stores/addPlaneWizardStore'
import type { ClientType } from '../wizard-schemas'

// Company sub-type options (shown when Company toggle is active)
const COMPANY_SUBTYPES: { value: ClientType; label: string }[] = [
  { value: 'corporate', label: 'Corporate' },
  { value: 'government', label: 'Government' },
  { value: 'non_profit', label: 'Non-Profit' },
]

export function ClientStep() {
  const {
    selectedClientId,
    selectedContactId,
    isCreatingNewClient,
    isCreatingNewContactForExistingClient,
    newClientData,
    newContactData,
    setSelectedClient,
    setSelectedContact,
    toggleCreateNewClient,
    toggleCreateNewContactForExistingClient,
    setNewClientData,
    setNewContactData,
  } = useAddPlaneWizardStore()

  const [clientSearchOpen, setClientSearchOpen] = useState(false)
  const [clientSearchQuery, setClientSearchQuery] = useState('')

  // Track whether user has manually edited contact name fields
  const contactNameEditedRef = useRef({ first_name: false, last_name: false })

  // Fetch clients for search
  const { data: clients, isLoading: isLoadingClients } = useClients()

  // Fetch contacts for selected client
  const { data: contacts, isLoading: isLoadingContacts } = useContacts(
    selectedClientId || undefined
  )

  // Auto-select primary contact once when contacts load for a newly selected client
  const autoSelectedForClientRef = useRef<string | null>(null)
  useEffect(() => {
    if (
      selectedClientId &&
      selectedClientId !== autoSelectedForClientRef.current &&
      !isCreatingNewContactForExistingClient &&
      contacts &&
      contacts.length > 0
    ) {
      const primaryContact = contacts.find((c) => c.is_primary_contact)
      if (primaryContact) {
        setSelectedContact(primaryContact.id)
      }
      autoSelectedForClientRef.current = selectedClientId
    }
    if (!selectedClientId) {
      autoSelectedForClientRef.current = null
    }
  }, [selectedClientId, isCreatingNewContactForExistingClient, contacts, setSelectedContact])

  // Filter clients based on search query
  const filteredClients = useMemo(() => {
    if (!clients) return []
    if (!clientSearchQuery) return clients.slice(0, 10) // Show first 10 by default

    const query = clientSearchQuery.toLowerCase()
    return clients
      .filter((client) => {
        const name = client.client_type === 'individual'
          ? `${client.first_name || ''} ${client.last_name || ''}`
          : client.company_name || ''
        return (
          name.toLowerCase().includes(query) ||
          client.email_primary?.toLowerCase().includes(query) ||
          client.client_number?.toLowerCase().includes(query)
        )
      })
      .slice(0, 10)
  }, [clients, clientSearchQuery])

  // Get selected client details
  const selectedClient = useMemo(() => {
    if (!selectedClientId || !clients) return null
    return clients.find((c) => c.id === selectedClientId)
  }, [clients, selectedClientId])

  // Get display name for client
  const getClientDisplayName = (client: NonNullable<typeof selectedClient>) => {
    if (client.client_type === 'individual') {
      return `${client.first_name || ''} ${client.last_name || ''}`.trim()
    }
    return client.company_name || 'Unknown Client'
  }

  // Handle client selection
  const handleClientSelect = (clientId: string) => {
    setSelectedClient(clientId)
    setClientSearchOpen(false)
    setClientSearchQuery('')
  }

  // Handle contact selection
  const handleContactSelect = (contactId: string) => {
    setSelectedContact(contactId === 'none' ? null : contactId)
  }

  // Handle new client form changes
  const handleNewClientChange = (field: keyof NewClientData, value: string) => {
    const updated = {
      ...(newClientData || { client_type: 'individual' }),
      [field]: value,
    } as NewClientData

    setNewClientData(updated)

    // Auto-sync individual client name → contact name (unless user manually edited)
    const isIndividual = updated.client_type === 'individual'
    if (isIndividual && (field === 'first_name' || field === 'last_name')) {
      if (!contactNameEditedRef.current[field]) {
        setNewContactData({
          ...(newContactData || { first_name: '', last_name: '', email_primary: '' }),
          [field]: value,
        } as NewContactData)
      }
    }

    // Reset contact name sync tracking when switching to individual
    if (field === 'client_type' && value === 'individual') {
      contactNameEditedRef.current = { first_name: false, last_name: false }
    }
  }

  // Handle new contact form changes
  const handleNewContactChange = (field: keyof NewContactData, value: string) => {
    // Mark contact name fields as manually edited
    if (field === 'first_name' || field === 'last_name') {
      contactNameEditedRef.current[field] = true
    }

    setNewContactData({
      ...(newContactData || {
        first_name: '',
        last_name: '',
        email_primary: '',
      }),
      [field]: value,
    } as NewContactData)
  }

  return (
    <div className="space-y-6">
      {/* Client mode: Search existing vs Create new */}
      <Tabs
        value={isCreatingNewClient ? 'create' : 'search'}
        onValueChange={(v) => toggleCreateNewClient(v === 'create')}
      >
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="search" className="flex gap-2">
            <Search className="w-4 h-4" />
            Select Existing
          </TabsTrigger>
          <TabsTrigger value="create" className="flex gap-2">
            <Plus className="w-4 h-4" />
            Create New
          </TabsTrigger>
        </TabsList>

        {/* Existing Client Selection */}
        <TabsContent value="search">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Search Client</Label>
              <Popover open={clientSearchOpen} onOpenChange={setClientSearchOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={clientSearchOpen}
                    className={cn(
                      'w-full justify-between font-normal',
                      !selectedClient && 'text-muted-foreground'
                    )}
                  >
                    <div className="flex items-center gap-2 truncate">
                      {selectedClient ? (
                        <>
                          {selectedClient.client_type === 'individual' ? (
                            <User className="w-4 h-4 shrink-0" />
                          ) : (
                            <Building2 className="w-4 h-4 shrink-0" />
                          )}
                          <span className="truncate">
                            {getClientDisplayName(selectedClient)}
                          </span>
                        </>
                      ) : (
                        <>
                          <Search className="w-4 h-4 shrink-0 opacity-50" />
                          <span>Search by name, email, or ID...</span>
                        </>
                      )}
                    </div>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[450px] p-0" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder="Search clients..."
                      value={clientSearchQuery}
                      onValueChange={setClientSearchQuery}
                    />
                    <CommandList>
                      {isLoadingClients && (
                        <div className="flex items-center justify-center py-6 gap-2 text-sm text-muted-foreground">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Loading clients...
                        </div>
                      )}

                      {!isLoadingClients && filteredClients.length === 0 && (
                        <CommandEmpty>
                          No clients found.{' '}
                          <button
                            type="button"
                            className="text-primary hover:underline"
                            onClick={() => {
                              toggleCreateNewClient(true)
                              setClientSearchOpen(false)
                            }}
                          >
                            Create new client
                          </button>
                        </CommandEmpty>
                      )}

                      {!isLoadingClients && filteredClients.length > 0 && (
                        <CommandGroup heading="Clients">
                          {filteredClients.map((client) => (
                            <CommandItem
                              key={client.id}
                              value={client.id}
                              onSelect={() => handleClientSelect(client.id)}
                              className="flex items-center justify-between gap-2 cursor-pointer"
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                {client.client_type === 'individual' ? (
                                  <User className="h-4 w-4 shrink-0 text-muted-foreground" />
                                ) : (
                                  <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                                )}
                                <div className="flex flex-col min-w-0">
                                  <span className="font-medium truncate">
                                    {getClientDisplayName(client)}
                                  </span>
                                  {client.email_primary && (
                                    <span className="text-xs text-muted-foreground truncate">
                                      {client.email_primary}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <Badge variant="secondary" className="text-xs">
                                  {client.client_number}
                                </Badge>
                                {selectedClientId === client.id && (
                                  <Check className="h-4 w-4" />
                                )}
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      )}
                    </CommandList>
                    {/* Sticky "Create New Client" at bottom of dropdown */}
                    <div className="p-2 border-t bg-muted/50">
                      <Button
                        variant="ghost"
                        className="w-full justify-start"
                        onClick={() => {
                          toggleCreateNewClient(true)
                          setClientSearchOpen(false)
                        }}
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        Create New Client
                      </Button>
                    </div>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Contact Selection/Creation (for existing client) */}
            {selectedClient && (
              <Tabs
                value={isCreatingNewContactForExistingClient ? 'create' : 'search'}
                onValueChange={(v) => toggleCreateNewContactForExistingClient(v === 'create')}
              >
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="search" className="flex gap-2">
                    <Search className="w-4 h-4" />
                    Select Contact
                  </TabsTrigger>
                  <TabsTrigger value="create" className="flex gap-2">
                    <Plus className="w-4 h-4" />
                    Add New
                  </TabsTrigger>
                </TabsList>

                {/* Existing Contact Dropdown */}
                <TabsContent value="search">
                  <div className="space-y-2">
                    <Label>Contact (Optional)</Label>
                    <Select
                      value={selectedContactId || 'none'}
                      onValueChange={handleContactSelect}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select a contact..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No specific contact</SelectItem>
                        {isLoadingContacts ? (
                          <div className="flex items-center justify-center py-2 gap-2 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Loading...
                          </div>
                        ) : (
                          contacts?.map((contact) => (
                            <SelectItem key={contact.id} value={contact.id}>
                              {contact.first_name} {contact.last_name}
                              {contact.is_primary_contact && (
                                <span className="text-muted-foreground ml-1">(Primary)</span>
                              )}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </TabsContent>

                {/* New Contact Form (inline) */}
                <TabsContent value="create">
                  <div className="space-y-4 p-4 border rounded-lg">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <User className="w-4 h-4" />
                      New Contact for {getClientDisplayName(selectedClient)}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>First Name *</Label>
                        <Input
                          placeholder="Jane"
                          value={newContactData?.first_name || ''}
                          onChange={(e) =>
                            handleNewContactChange('first_name', e.target.value)
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Last Name *</Label>
                        <Input
                          placeholder="Smith"
                          value={newContactData?.last_name || ''}
                          onChange={(e) =>
                            handleNewContactChange('last_name', e.target.value)
                          }
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        placeholder="jane@example.com"
                        value={newContactData?.email_primary || ''}
                        onChange={(e) =>
                          handleNewContactChange('email_primary', e.target.value)
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Phone (Optional)</Label>
                      <Input
                        type="tel"
                        placeholder="+1 (555) 000-0000"
                        value={newContactData?.phone_mobile || ''}
                        onChange={(e) =>
                          handleNewContactChange('phone_mobile', e.target.value)
                        }
                      />
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            )}
          </div>
        </TabsContent>

        {/* New Client Form */}
        <TabsContent value="create">
          <div className="space-y-6">
          {/* Client Type Toggle */}
          {(() => {
            const clientType = newClientData?.client_type || 'individual'
            const isIndividual = clientType === 'individual'
            return (
              <>
                <div className="space-y-2">
                  <Label>Client Type</Label>
                  <div className="flex items-center rounded-lg border p-1 gap-1">
                    <button
                      type="button"
                      className={cn(
                        'flex-1 flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                        isIndividual
                          ? 'bg-primary text-primary-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                      )}
                      onClick={() => handleNewClientChange('client_type', 'individual')}
                    >
                      <User className="w-4 h-4" />
                      Individual
                    </button>
                    <button
                      type="button"
                      className={cn(
                        'flex-1 flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                        !isIndividual
                          ? 'bg-primary text-primary-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                      )}
                      onClick={() => handleNewClientChange('client_type', 'corporate')}
                    >
                      <Building2 className="w-4 h-4" />
                      Company
                    </button>
                  </div>
                </div>

                {/* Company sub-type selector */}
                {!isIndividual && (
                  <div className="space-y-2">
                    <Label>Organization Type</Label>
                    <Select
                      value={clientType}
                      onValueChange={(value) =>
                        handleNewClientChange('client_type', value as ClientType)
                      }
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {COMPANY_SUBTYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Name fields (conditional based on type) */}
                {isIndividual ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>First Name *</Label>
                      <Input
                        placeholder="John"
                        value={newClientData?.first_name || ''}
                        onChange={(e) =>
                          handleNewClientChange('first_name', e.target.value)
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Last Name *</Label>
                      <Input
                        placeholder="Doe"
                        value={newClientData?.last_name || ''}
                        onChange={(e) =>
                          handleNewClientChange('last_name', e.target.value)
                        }
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label>Company Name *</Label>
                    <Input
                      placeholder="Acme Aviation Inc."
                      value={newClientData?.company_name || ''}
                      onChange={(e) =>
                        handleNewClientChange('company_name', e.target.value)
                      }
                    />
                  </div>
                )}
              </>
            )
          })()}

          {/* Contact Email */}
          <div className="space-y-2">
            <Label>Email (Optional)</Label>
            <Input
              type="email"
              placeholder="contact@example.com"
              value={newClientData?.email_primary || ''}
              onChange={(e) =>
                handleNewClientChange('email_primary', e.target.value)
              }
            />
          </div>

          {/* Contact Info (Collapsible) */}
          <Collapsible defaultOpen className="border rounded-lg">
            <CollapsibleTrigger asChild>
              <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  <span className="font-medium">Primary Contact *</span>
                </div>
                <Badge variant="secondary">Required</Badge>
              </div>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="p-4 pt-0 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>First Name *</Label>
                    <Input
                      placeholder="Jane"
                      value={newContactData?.first_name || ''}
                      onChange={(e) =>
                        handleNewContactChange('first_name', e.target.value)
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Last Name *</Label>
                    <Input
                      placeholder="Smith"
                      value={newContactData?.last_name || ''}
                      onChange={(e) =>
                        handleNewContactChange('last_name', e.target.value)
                      }
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    placeholder="jane@example.com"
                    value={newContactData?.email_primary || ''}
                    onChange={(e) =>
                      handleNewContactChange('email_primary', e.target.value)
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone (Optional)</Label>
                  <Input
                    type="tel"
                    placeholder="+1 (555) 000-0000"
                    value={newContactData?.phone_mobile || ''}
                    onChange={(e) =>
                      handleNewContactChange('phone_mobile', e.target.value)
                    }
                  />
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default ClientStep
